import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { ContainerHeight, ContainerType } from '@yard/app/api'
import { TransportSelectionProps } from '@yard/app/models/host/transport-selection.model'
import { ContainerHeightAutoComplete } from '@yard/components/AutoCompletes/ContainerHeightAutoComplete/ContainerHeightAutoComplete'
import {
  ContainerLength,
  ContainerLengthAutoComplete,
} from '@yard/components/AutoCompletes/ContainerLengthAutoComplete/ContainerLengthAutoComplete'
import { ContainerTypeAutoComplete } from '@yard/components/AutoCompletes/ContainerTypeAutoComplete/ContainerTypeAutoComplete'
import { BooleanSelectOption } from '@yard/utils/boolean-select.util'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode } from 'react'
import { AllocationRuleFormStore } from '../../stores/allocation-rule.form-store'
import { AllocationRuleFacetBooleanSelect } from '../molecules/AllocationRuleFacetBooleanSelect'

type Props = {
  formStore: AllocationRuleFormStore
  hideCarrierFields?: boolean
  disableTransportSelection?: boolean
  transportSelection?: (props: TransportSelectionProps) => ReactNode
  companyAutoComplete: (props: any) => ReactNode
  locationCodeAutoComplete: (props: any) => ReactNode
  imoClassAutoComplete: (props: any) => ReactNode
}

export const AllocationRuleFacetsSubForm: FC<Props> = observer(props => {
  const {
    formStore,
    hideCarrierFields,
    disableTransportSelection,
    transportSelection,
    companyAutoComplete,
    locationCodeAutoComplete,
    imoClassAutoComplete,
  } = props
  const facets = formStore.facets
  const { t } = useTranslate()
  const isDangerousYes = facets.isDangerous.value === BooleanSelectOption.True

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='subtitle2'>{t('facets', 'Facets')}</Typography>
      </Grid>

      {!hideCarrierFields && transportSelection && (
        <Grid size={12}>
          {transportSelection({
            disabled: disableTransportSelection,
            direction: formStore.facets.direction.value,
            carrierType: formStore.facets.carrierType.value ?? undefined,
            carrierVisitId: formStore.facets.carrierVisitId.value ?? undefined,
            onChangeCarrierType: (value: any) => formStore.facets.carrierType.setValue(value),
            onChangeCarrierVisit: (value: any) => formStore.facets.carrierVisitId.setValue(value),
          })}
        </Grid>
      )}

      <Grid size={4}>
        <ContainerLengthAutoComplete
          defaultValue={
            formStore.facets.size.value
              ? (formStore.facets.size.value as ContainerLength)
              : undefined
          }
          onChange={v => {
            const value = v as ContainerLength
            formStore.facets.size.setValue(value)
          }}
        />
      </Grid>
      <Grid size={4}>
        <ContainerHeightAutoComplete
          defaultValue={formStore.facets.containerHeight.value ?? undefined}
          onChange={value => {
            formStore.facets.containerHeight.setValue(value as ContainerHeight)
          }}
        />
      </Grid>
      <Grid size={4}>
        <ContainerTypeAutoComplete
          defaultValue={formStore.facets.containerType.value ?? undefined}
          onChange={value => {
            formStore.facets.containerType.setValue(value as ContainerType)
          }}
        />
      </Grid>

      <Grid size={4}>
        {companyAutoComplete?.({
          filterByTypes: ['ShippingLine'],
          label: t('containerOperator', 'Container operator'),
          selectedReferenceId: formStore.facets.containerOperatorId.value,
          handleOnChange: (value: any) =>
            formStore.facets.containerOperatorId.setValue(value?.referenceId),
        })}
      </Grid>
      <Grid size={4}>
        {companyAutoComplete?.({
          filterByTypes: ['ShippingLine'],
          label: t('customer', 'Customer'),
          selectedReferenceId: formStore.facets.customerId.value,
          handleOnChange: (value: any) => formStore.facets.customerId.setValue(value?.referenceId),
        })}
      </Grid>
      <Grid size={4}>
        {companyAutoComplete?.({
          filterByTypes: ['Consignee'],
          label: t('consignee', 'Consignee'),
          selectedReferenceId: formStore.facets.consigneeId.value,
          handleOnChange: (value: any) => formStore.facets.consigneeId.setValue(value?.referenceId),
        })}
      </Grid>

      <Grid size={6}>
        <FormControl fullWidth>
          <InputLabel>{t('cargoStatus', 'Cargo status')}</InputLabel>
          <Select {...facets.isEmpty.props} label={t('cargoStatus', 'Cargo status')}>
            <MenuItem value={BooleanSelectOption.False}>{t('full', 'Full')}</MenuItem>
            <MenuItem value={BooleanSelectOption.True}>{t('empty', 'Empty')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={6}>
        {locationCodeAutoComplete?.({
          label: t('portOfDischarge', 'Port of Discharge'),
          defaultValue: formStore.facets.portOfDischarge.value,
          onChange: (value: any) => formStore.facets.portOfDischarge.setValue(value),
        })}
      </Grid>

      <Grid size={6}>
        <AllocationRuleFacetBooleanSelect label={t('reefer', 'Reefer')} field={facets.isReefer} />
      </Grid>
      <Grid size={6}>
        <FormControl fullWidth>
          <InputLabel error={!!facets.isDangerous.error}>{t('dangerous', 'Dangerous')}</InputLabel>
          <Select
            {...facets.isDangerous.props}
            label={t('dangerous', 'Dangerous')}
            onChange={e => {
              const newValue = e.target.value as BooleanSelectOption | undefined
              facets.isDangerous.setValue(newValue)
              if (newValue !== BooleanSelectOption.True) {
                formStore.clearDangerousGoodsFields()
              }
            }}
          >
            <MenuItem value={BooleanSelectOption.True}>{t('dangerous', 'Dangerous')}</MenuItem>
            <MenuItem value={BooleanSelectOption.False}>
              {t('nonDangerous', 'Non Dangerous')}
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={6}>
        <AllocationRuleFacetBooleanSelect label={t('oog', 'OOG')} field={facets.isOOG} />
      </Grid>
      <Grid size={6}>
        <AllocationRuleFacetBooleanSelect label={t('restow', 'Restow')} field={facets.isRestow} />
      </Grid>

      {isDangerousYes && (
        <>
          <Grid size={12}>
            {imoClassAutoComplete({
              value: facets.imoClasses.value,
              onChange: (values: string[]) => facets.imoClasses.setValue(values),
              placeholder: t('allDgClasses', 'All DG classes (no restriction)'),
            })}
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth error={!!facets.enforcementMode.error}>
              <InputLabel>{t('enforcementMode', 'Enforcement Mode')}</InputLabel>
              <Select
                value={facets.enforcementMode.value ?? ''}
                label={t('enforcementMode', 'Enforcement Mode')}
                onChange={e => {
                  const value = e.target.value as 'Block' | 'Warn' | ''
                  facets.enforcementMode.setValue(value === '' ? null : value)
                }}
              >
                <MenuItem value='Block'>{t('enforcementModeBlock', 'Block — hard stop')}</MenuItem>
                <MenuItem value='Warn'>{t('enforcementModeWarn', 'Warn — advisory only')}</MenuItem>
              </Select>
              {facets.enforcementMode.error ? (
                <FormHelperText>
                  {t(
                    'enforcementModeRequired',
                    'Enforcement mode is required for dangerous goods rules',
                  )}
                </FormHelperText>
              ) : (
                facets.enforcementMode.value === null && (
                  <FormHelperText>
                    {t('enforcementModeHelperText', 'Select an enforcement mode to save this rule')}
                  </FormHelperText>
                )
              )}
            </FormControl>
          </Grid>
        </>
      )}
    </Grid>
  )
})
